import Vue from 'vue';
import Multiselect from 'vue-multiselect';
import MainVue from '../../main-vue';
import { createUpdateQuoteService, setItemSessionStorage } from '../../helper-functions/services';
Vue.component('multiselect', Multiselect);

new MainVue({
    el: '#app',
    data() {
        return {
            // Insurance plans dropdown (grouped for multiselect)
            insurancePlansGrouped: [],
            selectedPlan: null,
            isLoadingPlans: false,
            // Placeholder text for multiselect
            selectPlaceholder: window.globalLanguage?.claimsDropdownPlaceholder || 'Per Admin / Per Plan',

            // TOC
            tocExpanded: false,
            tocSelectedText: '',
            tocSelectedIcon: '',

            // FAQ items
            faqExpandedIndex: null,

            // Expandable sections
            expandedSections: {
                'types-insurance-content': false,
                'pre-claim-content': false,
                'how-to-use-content': false,
                'filing-process-content': false,
                'documentation-content': false,
                'processing-content': false,
                'denials-content': false,
                'denied-content': false
            },

            // Sticky card visibility
            showStickyCard: false,
            isTocFloating: false,
        }
    },
    mounted() {
        this.fetchInsurancePlans();
        this.initTocSelectedState();
        this.initStickyCardObserver();
        this.initTocStickyObserver();
        this.initTocScrollSpy();
        document.addEventListener('click', this.handleOutsideClick);
        // Allow deep-linking to a TOC section via URL hash (e.g. /…/#filing-process)
        this.activateTocFromHash();
        window.addEventListener('hashchange', this.activateTocFromHash);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideClick);
        window.removeEventListener('hashchange', this.activateTocFromHash);
        if (this.tocSpyHandler) {
            window.removeEventListener('scroll', this.tocSpyHandler);
        }
        if (this.heroCardObserver) {
            this.heroCardObserver.disconnect();
        }
        if (this.tocStickyObserver) {
            this.tocStickyObserver.disconnect();
        }
    },
    methods: {
        // Fetch insurance plans from API
        async fetchInsurancePlans() {
            this.isLoadingPlans = true;
            try {
                const response = await this.$http.get('/api/insurance-plans-list/');
                if (response.body && response.body.status === 'success' && response.body.data) {
                    // Group plans by company for vue-multiselect
                    const grouped = {};
                    response.body.data.forEach(plan => {
                        if (!grouped[plan.company]) {
                            grouped[plan.company] = [];
                        }
                        grouped[plan.company].push(plan);
                    });
                    // Convert to array format for vue-multiselect groups
                    this.insurancePlansGrouped = Object.keys(grouped).map(company => ({
                        company: company,
                        plans: grouped[company]
                    }));
                }
            } catch (error) {
                console.error('Error fetching insurance plans:', error);
            } finally {
                this.isLoadingPlans = false;
            }
        },

        // Handle plan selection change to target the specific document in the policy service
        onPlanSelect(selectedOption) {
            if (selectedOption) {
                // setItemSessionStorage('setclientCenterInsurancePlansList', selectedOption.id);
                // setItemSessionStorage('setclientCenterTasksList', 4);
                // setTimeout(()=>{window.location.href='/myaccount/?policyservice=true'},200);

                createUpdateQuoteService.call(this, window.ibJS.createUpdateQuoteAPIPath, {insurancePlan: selectedOption.id, taskToPerform: 4}, window.globalLanguage.sectionCriteriaKey, window.ibJS.quoteResultsPagePath);
            }
        },

        // TOC methods
        initTocSelectedState() {
            const toggleText = document.querySelector('.c-Claims-toc__toggle-text');
            const toggleIcon = document.querySelector('.c-Claims-toc__toggle-icon img');
            if (toggleText) this.tocSelectedText = this.tocDefaultText = toggleText.textContent;
            if (toggleIcon) this.tocSelectedIcon = this.tocDefaultIcon = toggleIcon.src;
        },

        // Update the toggle label/icon + active item. Shared by click and scroll spy.
        // iconSrc is optional; when omitted it's read from the TOC item's own icon.
        setActiveTocItem(item, iconSrc) {
            if (!item) return;
            const text = item.getAttribute('data-process');
            if (!iconSrc) {
                const iconImg = item.querySelector('.c-Claims-toc__item-icon img');
                iconSrc = iconImg ? iconImg.getAttribute('src') : null;
            }
            if (text != null) this.tocSelectedText = text;
            if (iconSrc) this.tocSelectedIcon = iconSrc;

            const toggleText = document.querySelector('.c-Claims-toc__toggle-text');
            const toggleIcon = document.querySelector('.c-Claims-toc__toggle-icon img');
            if (toggleText && text != null) toggleText.textContent = text;
            if (toggleIcon && iconSrc) toggleIcon.src = iconSrc;

            document.querySelectorAll('.c-Claims-toc__item').forEach(i => i.classList.remove('is-active'));
            item.classList.add('is-active');
        },

        // Restore the default toggle label (shown when scrolled above the first section).
        restoreDefaultToc() {
            const toggleText = document.querySelector('.c-Claims-toc__toggle-text');
            const toggleIcon = document.querySelector('.c-Claims-toc__toggle-icon img');
            if (toggleText && this.tocDefaultText != null) toggleText.textContent = this.tocDefaultText;
            if (toggleIcon && this.tocDefaultIcon) toggleIcon.src = this.tocDefaultIcon;
            document.querySelectorAll('.c-Claims-toc__item').forEach(i => i.classList.remove('is-active'));
        },

        // Scroll spy: as the page scrolls, set the TOC toggle to the section
        // currently under the activation line (just below the floating TOC).
        initTocScrollSpy() {
            const tocItems = Array.from(document.querySelectorAll('.c-Claims-toc__item'));
            // Pair each TOC item with its section element, in document order.
            const pairs = tocItems.map(item => {
                const href = item.getAttribute('href') || '';
                const sec = href.charAt(0) === '#' ? document.getElementById(href.slice(1)) : null;
                return sec ? { sec, item } : null;
            }).filter(Boolean);
            if (!pairs.length) return;

            let ticking = false;
            this.tocSpyHandler = () => {
                if (ticking) return;
                ticking = true;
                window.requestAnimationFrame(() => {
                    ticking = false;
                    // Activation line sits just below the (possibly floating) TOC,
                    // matching the resting position selectTocItem scrolls sections to.
                    const tocWrap = document.querySelector('.c-Claims-toc-wrap');
                    const line = (tocWrap ? tocWrap.offsetHeight : 150) + 16; // activation line
                    let active = null;
                    pairs.forEach(p => {
                        if (p.sec.getBoundingClientRect().top <= line) active = p;
                    });
                    if (active) this.setActiveTocItem(active.item);
                    else this.restoreDefaultToc();
                });
            };
            window.addEventListener('scroll', this.tocSpyHandler, { passive: true });
            this.tocSpyHandler(); // set initial state
        },

        toggleToc(event) {
            event.stopPropagation();
            this.tocExpanded = !this.tocExpanded;
        },

        selectTocItem(event, iconSrc) {
            event.preventDefault(); // Prevent default browser jump
            this.tocExpanded = false;

            // Update the toggle display + active state (shared with the scroll spy)
            this.setActiveTocItem(event.currentTarget, iconSrc);

            const processTarget = event.currentTarget.getAttribute('href');
            const processElm = document.querySelector(processTarget);
            if (processElm) {
                const processContent = processElm.querySelector(".c-Claims-types-section__content");
                if (processContent) {
                    const processId = processContent.id;
                    // Ensure section is expanded (using assignment instead of toggle)
                    this.expandedSections[processId] = true;
                    // Wait for Vue to expand the section (DOM update) then scroll
                    this.$nextTick(() => {
                        const top = processElm.getBoundingClientRect().top + window.pageYOffset;
                        // Define responsive offsets for "scrolling 100px more"
                        const tocWrap = document.querySelector('.c-Claims-toc-wrap');
                        let offset = 150; // Default fallback
                        if (tocWrap) {
                            const height = tocWrap.offsetHeight;
                            offset = height;
                        }

                        window.scrollTo({
                            top: top - offset,
                            behavior: 'smooth'
                        });
                    });
                }
            }
        },

        handleOutsideClick(event) {
            const tocNav = document.querySelector('.c-Claims-toc');
            if (tocNav && !tocNav.contains(event.target)) {
                this.tocExpanded = false;
            }
        },

        // Trigger the matching TOC item from the URL hash so deep links expand
        // and scroll to the section just like a click does.
        activateTocFromHash() {
            const hash = window.location.hash;
            if (!hash || hash.length < 2) return;
            this.$nextTick(() => {
                const tocItem = document.querySelector('.c-Claims-toc__item[href="' + hash + '"]');
                if (tocItem) tocItem.click();
            });
        },

        // FAQ methods
        toggleFaq(index) {
            if (this.faqExpandedIndex === index) {
                this.faqExpandedIndex = null;
            } else {
                this.faqExpandedIndex = index;
            }
        },

        isFaqExpanded(index) {
            return this.faqExpandedIndex === index;
        },

        // Expandable section methods
        toggleSection(sectionId) {
            this.expandedSections[sectionId] = !this.expandedSections[sectionId];
        },

        isSectionExpanded(sectionId) {
            return this.expandedSections[sectionId];
        },

        // Online chat handler
        openOnlineChat(event) {
            event.preventDefault();
            if (!GlobalVue.isOfficeOpen && window.ibJS.gcip != 'yes') {
                window.open('/insurance-assistant/', '_blank');
            } else if (typeof window.Genesys === 'function') {
                Genesys("command", "Messenger.open", {},
                    function(o){},  // if resolved
                    function(o){    // if rejected
                        Genesys("command", "Messenger.close");
                    }
                );
            }
        },

        // Sticky card observer - shows sidebar card when hero card scrolls out of view
        initStickyCardObserver() {
            const heroCard = document.querySelector('.c-Claims-hero__card');
            if (!heroCard) return;

            this.heroCardObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    // Show sticky card when hero card is not intersecting (scrolled out of view)
                    this.showStickyCard = !entry.isIntersecting;
                });
            }, {
                threshold: 0,
                rootMargin: '-10px 0px 0px 0px'
            });

            this.heroCardObserver.observe(heroCard);
        },
        initTocStickyObserver() {
            const tocWrap = document.querySelector('.c-Claims-toc-wrap');
            if (!tocWrap) return;
            // Insert a 1px sentinel just above the TOC to detect when it
            // has scrolled out of its natural flow position
            const sentinel = document.createElement('div');
            sentinel.setAttribute('aria-hidden', 'true');
            tocWrap.parentElement.insertBefore(sentinel, tocWrap);
            this.tocStickyObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    this.isTocFloating = !entry.isIntersecting;
                    tocWrap.classList.toggle('is-floating', this.isTocFloating);
                });
            }, { threshold: 0 });
            this.tocStickyObserver.observe(sentinel);
        },
    }
});
